import type { AiCapabilityPage } from "../schema.js";
import { LIVE, OWNER_ADMIN, SLOT_GROUP, WRITE_FLAGS } from "./_shared.js";

/**
 * `ONB-ADD-MEMBERS` (@UC0194) — thêm thành viên, dán được cả danh sách, T1. Executor = `MembersService.createSeats`
 * (`members.service.ts:1009`) — tạo SUẤT (seat) `unclaimed`, người thật gắn vào sau qua lời mời/IDENTITY-LINK.
 * Cùng cửa GUI `POST /v1/groups/:id/members` (`groups.controller.ts:212`), gác `@RequireGroupRoles(OWNER, ADMIN)`
 * ⇒ vai trang = O/A, KHÔNG thủ quỹ.
 *
 * 🔴🔴 TRANG NÀY CHỈ CHẠY Ở NHÓM `parent` — 3/9 loại nhóm mà CHÍNH chat tạo ra được (US-AIV2-0033 B0-2, QĐ điều phối
 * 18/09 chọn phương án A). Đây là SỰ THẬT của cửa, không phải hạn chế tạm:
 *   · `createSeats` ném **422 `GROUP_SEATS_PARENT_ONLY`** khi `group.type !== "parent"` (`members.service.ts:1023`) —
 *     nhóm `standard` là 1-1 nên suất-không-người ở đó là ghế trống vĩnh viễn, làm sĩ số nói dối.
 *   · Đối chiếu `ONB_CREATE_GROUP_CATEGORY_CODES` (binding 0031) với `group_categories`: `parent` = `parent_class`,
 *     `family_travel`, `family_couple` (**3**); `standard` = `company_club`, `friends`, `sports_team`, `solo_travel`,
 *     `individual_person`, `hometown` (**6**).
 *   · Đường thêm người cho nhóm `standard` (`addFromQueue`, `POST /groups/:id/members/add`) **bị CẤM với AI có chủ đích**:
 *     closed-set `GLOBAL_ADD_ALLOWED_SURFACES` đúng 1 phần tử, mặc định ⇒ **403**, khoá bằng
 *     `add-global-ai-block.invariants.spec.ts` (lưới quét TOÀN `apps/api/src`, không chỉ `modules/ai`).
 * ⇒ Hệ quả người đọc Sổ PHẢI thấy: chuỗi `ONB-CREATE-GROUP → ONB-CREATE-FUND → ONB-ADD-MEMBERS` **gãy ở bước 3 cho 6/9
 * loại nhóm**. Ở đó trang KHÔNG dựng thẻ xác nhận mà từ chối **nói rõ lý do** + dẫn sang màn GUI (QĐ PO 17/09 16:05:
 * *từ chối phải nói rõ*, không 403 trần) — chốt nằm ở `preview()` của binding, tức TRƯỚC khi thẻ được dựng. Nửa `standard`
 * là **task riêng**, không phải quên.
 *
 * 🔴 `endpoints` GIỮ cả `POST …/members/add` — và đây là quyết định đã ĐO LẠI, không phải sót (B0-4 của 0033 ban đầu đề
 * nghị gỡ; đo tiếp thì sai): bản đồ `@UC0216` là bản đồ KÍN (mọi cửa `apps/mobile` thuộc ĐÚNG một trang — gate G14f), và
 * `apps/mobile` CÓ caller cửa đó (`groups_endpoints.dart:68`). Gỡ khỏi đây = một cửa mồ côi ⇒ gate đỏ. Trang vẫn "chịu
 * trách nhiệm" cửa đó theo đúng nghĩa người dùng: hỏi "thêm người" ở nhóm `standard` thì nhận **câu từ chối có lý do +
 * nút mở màn GUI**, không phải im lặng. Cái trang KHÔNG làm là **tự gọi** cửa đó — closed-set chặn, và đúng ra phải chặn.
 *
 * 🔴🔴 Ô `danhSach` khai `duplicates: "ask"` — ô `list` ĐẦU TIÊN bật chế độ này (US-AIV2-0033 B0-5). Mặc định của ô `list`
 * là `drop` (gộp mục trùng, hành vi từ 0022, **0 regress**). Ở đây gộp là SAI và sai vào tiền: lớp phụ huynh có hai bé
 * cùng tên "Minh" mà bị gộp ⇒ mất một suất, và vì `createSeats` gọi `autoEnroll.enrollMembers` ghi `member_dues` **trong
 * cùng tx**, một gia đình **không bao giờ nhận nghĩa vụ đóng**. Thẻ in "29 người" nên phép đối chiếu facts vẫn khớp và
 * **không ai thấy mình bị mất** — đúng thứ lưới không bắt được nếu không bật chế độ này.
 * ⚠️ Khai đúng tên: đây là **lặp-khi-TẠO**, KHÔNG phải phân giải `memberId` khi tham chiếu người đang có (cam kết #4).
 * **Trang này KHÔNG chứng minh cam kết #4** — nó không có ô `type: "member"` nào. Trang gánh cam kết #4 là
 * `RECORD-PAID` (@UC0200), theo QĐ điều phối 18/09.
 *
 * 🔴🔴 TÊN HỌC SINH ĐI TỚI LLM — **QĐ PO 18/09/2026, đã ghi `CLAUDE.md §4`** (develop `f8cb183af`). Trang này là trang
 * `bound` ĐẦU TIÊN mang một DANH SÁCH TÊN NGƯỜI, và `interpret()` được gọi **vô điều kiện** trước mọi nhánh, nên danh
 * sách vừa dán đi thẳng tới LLM bên thứ ba. Ba vế của QĐ, chép đúng để người sau không "sửa lại cho đúng":
 *   1. Đây là **GHI LẠI HIỆN TRẠNG, không phải NỚI** một chốt nào — scrubber **chưa bao giờ** cạo tên người.
 *   2. **Ba loại PII còn lại KHÔNG đổi**: email · điện thoại · số tài khoản vẫn scrub TRƯỚC khi gửi, vẫn có test
 *      mutation-proof ở `base-llm-client`. Đọc QĐ này thành *"thôi scrub PII"* là **SAI**.
 *   3. Rủi ro **chấp nhận có chủ đích**; `rawPrompt` vẫn lưu thô ở `ai_audit_log` **append-only** ⇒ **không scrub
 *      được về sau**.
 * Điều kiện XEM LẠI: yêu cầu pháp lý/hợp đồng xử lý dữ liệu · trang mới mang danh sách người quy mô lớn hơn · đổi nhà
 * cung cấp LLM. Hai phương án đã cân và **không chọn**: bỏ gọi LLM cho ô `list`; cạo tên trước khi gửi.
 * ⚠️ Bài học chung cho MỌI trang `bound` sau (điều phối 18/09): hỏi **"lượt này gửi gì RA NGOÀI?"** TRƯỚC khi hỏi
 * "lượt này ghi gì vào DB" — đây không phải bug ai viết sai, nó là một QUYẾT ĐỊNH CHƯA AI RA đang chạy im lặng.
 *
 * 🔴 GỬI TỆP ĐƯỢC — US-AIV2-0121 (@UC0390), ĐẢO QĐ PO 17/09 09:20 mục 3 (*"chat không có ô đính kèm"*) theo yêu cầu người dùng 15/10/2026.
 * Ô `danhSach` khai `fileImport: member_names` ⇒ lượt hỏi ô mang `slot.fileImport` ⇒ app hiện nút đính kèm ⇒ `POST /v1/ai/v2/tasks/:taskId/file`
 * đọc tệp TẤT ĐỊNH (`parseMemberFile`, CSV/TXT) rồi điền ô qua đúng đường chip `choose` — **0 lời gọi LLM, tên trong tệp không tới LLM**, và
 * không vướng trần span 400 ký tự của câu dán. Hạ nguồn KHÔNG đổi một dòng: hỏi tên lặp (`duplicateAsk`) · trần 1..100 của binding · thẻ
 * xác nhận · `confirm()` gác ×2 · `createSeatsInTx`. Phạm vi CỐ Ý hẹp (điều phối quyết theo uỷ quyền 09/10, đảo được):
 *   · CSV/TXT thôi. `.xlsx` cần thư viện đọc zip mới cho `apps/api` (review bảo mật: zip bomb · công thức) ⇒ task sau.
 *   · ẢNH vẫn đi màn GUI (`MEMBER-SCAN`): màn đó có bước duyệt TỪNG DÒNG (cảnh báo tên đáng ngờ · độ tin cậy thấp), thẻ chat chỉ in 10 tên —
 *     đưa OCR thẳng vào chat là bỏ lớp duyệt đó.
 * `ask` của ô GIỮ NGUYÊN chữ cũ (nó vào prompt ⇒ sửa = mục `promptHash` của ca eval); câu người dùng THẤY là `fileImport.ask` (`askVariant`).
 */
export const ONB_ADD_MEMBERS = {
  id: "ONB-ADD-MEMBERS",
  uc: "@UC0194",
  tier: "T1",
  status: LIVE,
  roles: OWNER_ADMIN,
  flags: WRITE_FLAGS,
  slots: [
    SLOT_GROUP,
    {
      id: "danhSach",
      type: "list",
      required: true,
      ask: "Dán danh sách tên (mỗi dòng một người, hoặc cách nhau bằng dấu phẩy). Dán từ Excel được; gửi file thì chưa được.",
      duplicates: "ask",
      fileImport: {
        kind: "member_names",
        ask: "Dán danh sách tên (mỗi dòng một người, hoặc cách nhau bằng dấu phẩy), hoặc bấm nút đính kèm để gửi tệp CSV/TXT có cột Họ và tên.",
      },
      // Câu này là thứ người dùng đọc khi danh sách có tên lặp. Nó phải nói RÕ phải làm gì, vì engine sẽ hỏi lại bằng
      // chính nó: một câu chung chung ⇒ người dùng dán y nguyên ⇒ vòng lặp. Không nêu tên cụ thể — lý do KỸ THUẬT:
      // `token` của resolver không được lưu vào phiếu nên tầng render không có tên để chèn.
      duplicateAsk:
        "Trong danh sách có tên bị lặp. Nếu là hai người khác nhau, thêm chữ cho khác nhau (ví dụ: Minh A, Minh B) rồi gửi lại; nếu gõ nhầm thì bỏ bớt một dòng.",
      // 🔴 Vòng 2 (review #842 HIGH): span của LLM có TRẦN 400 ký tự, mà 30 tên Việt đầy đủ ≈ 405 ⇒ lớp 30 cháu nằm
      // ngay trên ranh giới, lớp 35–40 rơi qua chắc chắn. Nhận span đã cắt = ghi thiếu người + thiếu `member_dues`,
      // mà thẻ vẫn nói một con số tự tin. Hỏi lại, KHÔNG đoán.
      partialAsk:
        "Mình mới đọc được một phần danh sách bạn dán, sợ thiếu người nên chưa dám ghi. Bạn gửi lại danh sách giúp mình nhé — nếu lớp đông thì chia làm hai lần gửi cũng được.",
    },
  ],
  llm: {
    description:
      "Thêm một hoặc nhiều NGƯỜI vào nhóm phụ huynh bằng tên — người dùng thường dán cả danh sách lớp. Người dùng hay nói \"thêm mấy bạn vào quỹ lớp\" thay vì \"vào nhóm\"; cả hai đều là việc này, vì thêm người chứ không phải mở đợt thu tiền. Chỉ nhóm dạng phụ huynh/gia đình.",
    examples: [
      "thêm thành viên vào nhóm",
      "them thanh vien vao nhom",
      "thêm Minh, Lan, Hùng vào nhóm",
      "them minh lan hung",
      "dán danh sách lớp vào đây",
      "dan danh sach lop",
      "add người vào nhóm",
      "thêm người",
      "nhập danh sách phụ huynh",
      "nhap danh sach phu huynh",
      "cho thêm 30 bạn lớp 3A vào",
      "add member",
      // ——— 0105: thêm cho đủ 40 ———
      "them nguoi",
      "cho them 30 ban lop 3A vao",
      "add nguoi vao nhom",
      "thêm phụ huynh vào nhóm",
      "them phu huynh vao nhom",
      "thêm An và Bình vào nhóm giúp mình",
      "them An va Binh vao nhom giup minh",
      "tôi có danh sách lớp, thêm hết vào nhóm",
      "toi co danh sach lop them het vao nhom",
      "thêm các bạn trong lớp vào nhóm",
      "them cac ban trong lop vao nhom",
      "thêm thành viên mới",
      "them thanh vien moi",
      "cho mình thêm mấy người nữa vào nhóm",
      "cho minh them may nguoi nua vao nhom",
      "thêm người vào nhóm kiểu gì?",
      "them nguoi vao nhom kieu gi",
      "nhập tên các bạn vào nhóm",
      "nhap ten cac ban vao nhom",
      "thêm 5 bạn nữa vào quỹ lớp",
      "them 5 ban nua vao quy lop",
      "danh sách đây, thêm vào nhóm nhé",
      "danh sach day them vao nhom nhe",
      "thêm Minh Lan Hùng An Bình",
      "them Minh Lan Hung An Binh",
      "bổ sung thành viên cho nhóm",
      "bo sung thanh vien cho nhom",
      "thêm tên vào danh sách nhóm",
      "them ten vao danh sach nhom",
    ],
  },
  executor: "members.createSeats",
  /**
   * `@UC0216` — cửa mobile trang này chịu trách nhiệm. `…/members` là cửa trang THỰC THI; `…/members/add` là cửa trang
   * TỪ CHỐI CÓ LÝ DO + dẫn GUI (xem docblock); `…/dues/quote` là xem trước nghĩa vụ khi thêm giữa kỳ.
   */
  endpoints: [
    "POST /groups/{groupId}/members",
    "POST /groups/{groupId}/members/add",
    "POST /groups/{groupId}/members/{memberId}/dues/quote",
  ],
  facts: ["soNguoi", "tenNhom"],
  templates: {
    // 🔴 US-AIV2-0111 C02 (@UC0194): thẻ LIỆT KÊ từng tên (nhãn ô `danhSach` = đúng danh sách resolver đã tách) — thẻ là lớp chốt thứ hai
    // khi câu trả lời được nhận nguyên văn (Người phiên dịch không nhận ra): người dùng thấy AI sắp thêm những ai trước khi bấm Đồng ý.
    confirm: "Thêm {soNguoi} người vào nhóm {tenNhom}: {danhSach}?",
    done: "Đã thêm {soNguoi} thành viên vào {tenNhom}. Gửi lời mời để họ vào app khi cần.",
  },
  journey: { outcomes: { done: { next: ["COL-EQUAL", "READ-PEOPLE-MINE"] } } },
  help: { title: "Thêm thành viên", tryNow: "thêm thành viên vào nhóm" },
  // 🔴 US-AIV2-0116 (@UC0194): nhóm THƯỜNG ⇒ Người làm từ chối (`GROUP_SEATS_PARENT_ONLY`, lõi chỉ tạo suất cho nhóm phụ huynh) và nút đi
  // tiếp mở THẲNG bước "Thêm thành viên" của màn nhóm (app #1039: tab Thành viên + bảng chọn cách thêm — tìm người đã có app bằng tên · mã ·
  // QR, hoặc thêm tên người chưa có app). Vai của nút = vai trang (O/A) ⇒ member không nhận nút này.
  guiScreen: "/groups/:groupId/add-member",
  screenShows: "member_add",
} satisfies AiCapabilityPage;
